import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn.js";

export const iconButtonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-xl border bg-transparent transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border-[rgba(15,23,42,0.09)] text-[#576274] hover:border-[rgba(147,51,245,0.4)] hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:border-white/20 dark:text-white dark:hover:border-white/30 dark:hover:bg-white/10",
        destructive:
          "border-red-400/35 text-red-300 hover:border-red-400/50 hover:bg-red-500/10 hover:text-red-200",
      },
      size: {
        xs: "h-5 w-5 [&_svg]:!size-3 sm:h-6 sm:w-6 sm:[&_svg]:!size-3",
        sm: "h-7 w-7 [&_svg]:!size-3.5 sm:h-8 sm:w-8 sm:[&_svg]:!size-4",
        default: "h-8 w-8 [&_svg]:!size-3.5 sm:h-9 sm:w-9 sm:[&_svg]:!size-4",
        lg: "h-9 w-9 [&_svg]:!size-4 sm:h-10 sm:w-10 sm:[&_svg]:!size-5",
      },
      active: {
        true: "border-[rgba(147,51,245,0.4)] bg-[var(--nav-active-bg)] text-[#0b1120] dark:border-white/35 dark:bg-white/10 dark:text-white",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "destructive",
        active: true,
        className: "border-red-400/50 bg-red-500/10 text-red-200",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      active: false,
    },
  }
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, active, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(iconButtonVariants({ variant, size, active, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
IconButton.displayName = "IconButton";
